import React, { useCallback, useContext, useEffect, useRef } from "react";
import { useLocation, useNavigate, matchPath } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ApiContext } from "../../context/MyContext";
import {
  markRoomMentioned,
  markTicketSeen,
  requestSupportResync,
  subscribeIncomingMessages,
} from "./RoomsUnreadProvider";
import { useUserChannel } from "./useUserChannel";
import { applyBulkSendEvent } from "../../features/chatDock/bulkSendStore";
import { parseBroadcastNotification } from "./supportTicketsSync";
import { TICKET_ALERTS } from "../../common/notifications/notificationText";
import {
  LEAD_EVENT,
  leadAlertTarget,
  leadAlertText,
} from "../../features/leads/core/utils/notifyText";
import { useCommsIdentity } from "./useCommsIdentity";
import MessageToast, { messagePreview } from "../../common/comms/MessageToast";
import {
  installSoundPrimer,
  playMentionSound,
  playNotificationSound,
} from "../../common/comms/notificationSound";
import {
  ensurePermission,
  showMessageNotification,
} from "../../common/comms/osNotifications";

// Isolated container id: the legacy <Toaster/> mounts its own per-instance
// ToastContainer transiently (e.g. Thread.jsx while an error toast is
// shown). Targeting every call here by containerId keeps message toasts out
// of Toaster's containers and vice versa for our targeted calls.
const CONTAINER_ID = "comms-notify";
const TOAST_AUTO_CLOSE_MS = 6000;
// A broadcast notification usually means ticket state changed somewhere
// (assignment, SLA, mention). Resync the support set shortly after — the
// debounce collapses event bursts into one refetch.
const NOTIFY_RESYNC_DEBOUNCE_MS = 1000;

/**
 * Lead alert types and their toast id prefix. Wording and target come from
 * `leadAlertText` / `leadAlertTarget`, shared with the bell. Lead events are
 * not ticket state, so they never fall into the support resync below.
 */
const LEAD_ALERTS = {
  [LEAD_EVENT.ASSIGNED]: { toastPrefix: "lead-assigned" },
  [LEAD_EVENT.REASSIGNED]: { toastPrefix: "lead-reassigned" },
  [LEAD_EVENT.STATUS]: { toastPrefix: "lead-status" },
  [LEAD_EVENT.ACTIVITY]: { toastPrefix: "lead-activity" },
  [LEAD_EVENT.OUTCOME]: { toastPrefix: "lead-outcome" },
  [LEAD_EVENT.CALLBACK]: { toastPrefix: "lead-callback" },
};

/**
 * Global notify layer for incoming chat messages, support-ticket messages,
 * new-ticket alerts and "ticket assigned to you" broadcasts: in-app toast on
 * any page, gesture-primed chime (played by RoomsUnreadProvider for messages),
 * and an OS-level notification
 * when the tab is hidden/unfocused. Support payloads are flagged with
 * `roomSummary.kind === "support"` (and `isNewTicket` for creation alerts)
 * and click through to `/support/tickets/:id` instead of `/messages/:id`.
 * Mounted once at the app root, inside the Router and below
 * RoomsUnreadProvider.
 */
export function CommsNotifyProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { empId } = useContext(ApiContext);
  const { getClientName, getCoachName } = useCommsIdentity();

  const locationRef = useRef(location);
  locationRef.current = location;

  // Prime the notification chime on the first user gesture (autoplay policy
  // blocks Web Audio until then).
  useEffect(() => installSoundPrimer(), []);

  // Ask for OS notification permission the first time the user shows intent
  // (visits the Messages or Support workspace) rather than at app boot.
  const permissionAskedRef = useRef(false);
  useEffect(() => {
    if (
      !permissionAskedRef.current &&
      (location.pathname.startsWith("/messages") ||
        location.pathname.startsWith("/support") ||
        location.pathname.startsWith("/leads"))
    ) {
      permissionAskedRef.current = true;
      ensurePermission();
    }
  }, [location.pathname]);

  const resolveSenderName = useCallback(
    (msg, roomSummary) => {
      if (msg?.sender_role === "COACH") {
        return getCoachName(msg.sender_id) || "New message";
      }
      return (
        roomSummary?.client_name ||
        getClientName(roomSummary?.client_id) ||
        "New message"
      );
    },
    [getClientName, getCoachName]
  );

  // Personal broadcast channel: react globally to the ticket lifecycle events
  // (toast + instant support resync so the ticket's room watcher mounts right
  // away). No chime here: RoomsUnreadProvider already plays one when the
  // ticket's own room watcher sees the message, and a second one on the
  // lifecycle event would double up on every assignment.
  const notifyResyncTimerRef = useRef(null);
  const handleBroadcastNotification = useCallback(
    (data) => {
      const parsed = parseBroadcastNotification(data);

      // A chat @-mention of the viewer — chat, not tickets, so it must never
      // reach the support-resync fallthrough below.
      if (parsed?.eventType === "MENTION") {
        const { roomId, subject, senderId, senderName } = parsed;
        if (roomId) {
          // Seed the inbox badge even when the room's watcher isn't mounted
          // yet (fresh session, room outside the fetched page, etc.).
          markRoomMentioned(roomId);
        }
        // Already reading that thread — the highlighted bubble is feedback
        // enough.
        const activeChatRoomId = matchPath(
          "/messages/:roomId",
          locationRef.current.pathname
        )?.params?.roomId;
        if (roomId && activeChatRoomId && String(activeChatRoomId) === String(roomId)) {
          return;
        }

        const name =
          (senderId && getCoachName(senderId)) || senderName || "A coach";
        const title = `${name} mentioned you`;
        const preview = subject || "Open the conversation";
        const goToThread = () =>
          navigate(roomId ? `/messages/${roomId}` : "/messages");

        playMentionSound();

        if (document.hidden || !document.hasFocus()) {
          showMessageNotification({
            title,
            body: preview,
            roomId: roomId || "mention",
            onClick: goToThread,
          });
          return;
        }

        // Same id as the generic new-message toast for this room, so the
        // mention UPGRADES it in place instead of stacking a duplicate.
        const toastId = `room-${roomId || "mention"}`;
        const content = <MessageToast senderName={title} preview={preview} />;
        const options = {
          containerId: CONTAINER_ID,
          toastId,
          autoClose: TOAST_AUTO_CLOSE_MS,
          closeOnClick: true,
          onClick: goToThread,
        };
        if (toast.isActive(toastId, CONTAINER_ID)) {
          toast.update(toastId, { ...options, render: content });
        } else {
          toast(content, options);
        }
        return;
      }

      const leadAlert = parsed ? LEAD_ALERTS[parsed.eventType] : null;
      if (leadAlert) {
        const { eventType, payload, eventId } = parsed;
        const { title, body } = leadAlertText(eventType, payload);
        const target = leadAlertTarget(eventType, payload);
        const key = payload?.lead_id ? String(payload.lead_id) : eventId || "unknown";
        const goToLead = () => navigate(target);

        playNotificationSound();

        if (document.hidden || !document.hasFocus()) {
          showMessageNotification({
            title,
            body,
            roomId: `lead-${key}`,
            onClick: goToLead,
          });
          return;
        }

        const toastId = `${leadAlert.toastPrefix}-${key}`;
        const content = <MessageToast senderName={title} preview={body} />;
        const options = {
          containerId: CONTAINER_ID,
          toastId,
          autoClose: TOAST_AUTO_CLOSE_MS,
          closeOnClick: true,
          onClick: goToLead,
        };
        if (toast.isActive(toastId, CONTAINER_ID)) {
          toast.update(toastId, { ...options, render: content });
        } else {
          toast(content, options);
        }
        return;
      }

      const alert = parsed ? TICKET_ALERTS[parsed.eventType] : null;

      if (!alert) {
        // Unknown / other event: still refresh ticket state, debounced.
        if (notifyResyncTimerRef.current) {
          clearTimeout(notifyResyncTimerRef.current);
        }
        notifyResyncTimerRef.current = setTimeout(
          () => requestSupportResync(),
          NOTIFY_RESYNC_DEBOUNCE_MS
        );
        return;
      }

      const { eventType, roomId, subject, clock } = parsed;
      if (roomId) {
        // The resync diff would otherwise raise a duplicate "New ticket"
        // alert for a ticket we're about to announce ourselves — and sooner.
        markTicketSeen(roomId);
      }
      if (notifyResyncTimerRef.current) {
        clearTimeout(notifyResyncTimerRef.current);
        notifyResyncTimerRef.current = null;
      }
      // Naming the ticket lets an open queue re-read that one row instead of
      // re-pulling its whole page for a single-ticket change.
      requestSupportResync({ roomId, eventType });

      // Already viewing that ticket (e.g. own pick-up echoing back) — the
      // action flow has its own feedback. A breach still deserves the alert:
      // the whole point is that it is news even to whoever is looking at it.
      const activeTicketId = matchPath(
        "/support/tickets/:roomId",
        locationRef.current.pathname
      )?.params?.roomId;
      if (
        eventType !== "SLA_BREACH" &&
        roomId &&
        activeTicketId &&
        String(activeTicketId) === String(roomId)
      ) {
        return;
      }

      const title = alert.title(clock);
      const preview = subject || alert.fallback;
      const goToTicket = () =>
        navigate(roomId ? `/support/tickets/${roomId}` : "/support/console");

      if (document.hidden || !document.hasFocus()) {
        showMessageNotification({
          title,
          body: preview,
          roomId: roomId || "ticket-assigned",
          onClick: goToTicket,
        });
        return;
      }

      const toastId = `${alert.toastPrefix}-${roomId || "unknown"}`;
      const content = <MessageToast senderName={title} preview={preview} />;
      const options = {
        containerId: CONTAINER_ID,
        toastId,
        autoClose: TOAST_AUTO_CLOSE_MS,
        closeOnClick: true,
        onClick: goToTicket,
      };
      if (toast.isActive(toastId, CONTAINER_ID)) {
        toast.update(toastId, { ...options, render: content });
      } else {
        toast(content, options);
      }
    },
    [navigate, getCoachName]
  );

  // Bulk-send progress is fed here rather than from the chat dock on purpose:
  // this provider is mounted app-wide, and the dock's panel unmounts the moment
  // it is closed. Routing progress through the panel would mean a send stopped
  // being tracked as soon as the coach looked away — the exact thing
  // fire-and-forget is supposed to make safe.
  useUserChannel(empId, {
    onNotification: handleBroadcastNotification,
    onBulkSend: applyBulkSendEvent,
  });

  useEffect(
    () => () => {
      if (notifyResyncTimerRef.current) {
        clearTimeout(notifyResyncTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const unsubscribe = subscribeIncomingMessages(({ roomId, msg, roomSummary }) => {
      const isSupport = roomSummary?.kind === "support";
      // Defense in depth — RoomsUnreadProvider already skips the active room.
      const activeRoomId = matchPath(
        isSupport ? "/support/tickets/:roomId" : "/messages/:roomId",
        locationRef.current.pathname
      )?.params?.roomId;
      if (activeRoomId && String(activeRoomId) === String(roomId)) {
        return;
      }

      let senderName;
      let preview;
      if (isSupport) {
        const ticketNo = roomSummary?.display_id
          ? `Ticket #${roomSummary.display_id}`
          : "Support ticket";
        const clientName =
          roomSummary?.client_name ||
          getClientName(roomSummary?.client_id) ||
          "Client";
        senderName = roomSummary?.isNewTicket
          ? `New ticket · ${ticketNo}`
          : `${ticketNo} · ${clientName}`;
        preview = roomSummary?.isNewTicket
          ? roomSummary?.subject || `From ${clientName}`
          : messagePreview(msg);
      } else {
        senderName = resolveSenderName(msg, roomSummary);
        preview = messagePreview(msg);
      }
      const goToThread = () =>
        navigate(isSupport ? `/support/tickets/${roomId}` : `/messages/${roomId}`);

      if (document.hidden || !document.hasFocus()) {
        // Backgrounded tab: the in-app toast would go unseen — raise an OS
        // notification instead (per-room tag collapses bursts).
        showMessageNotification({
          title: senderName,
          body: preview,
          roomId,
          onClick: goToThread,
        });
        return;
      }

      const toastId = isSupport ? `ticket-${roomId}` : `room-${roomId}`;
      const content = <MessageToast senderName={senderName} preview={preview} />;
      const options = {
        containerId: CONTAINER_ID,
        toastId,
        autoClose: TOAST_AUTO_CLOSE_MS,
        closeOnClick: true,
        onClick: goToThread,
      };
      // One toast per room: a burst updates the preview in place.
      if (toast.isActive(toastId, CONTAINER_ID)) {
        toast.update(toastId, { ...options, render: content });
      } else {
        toast(content, options);
      }
    });
    return unsubscribe;
  }, [navigate, resolveSenderName, getClientName]);

  return (
    <>
      {children}
      <ToastContainer
        containerId={CONTAINER_ID}
        position="bottom-right"
        limit={3}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss={false}
        style={{ zIndex: 60 }}
      />
    </>
  );
}
